import React, { useCallback, useEffect, useState } from 'react';
import { api } from '../utils/api';
import { toEasternDateShort } from '../utils/time';
import { useListFilter } from '../hooks/useListFilter';
import Tabs from '../components/Tabs';
import { PageHeader, StatCard, Panel, StatusPill, Empty, Skeleton, Icon, SortTh, useSort, usePager, Pager, useToast } from '../../components/ui';

const TABS = [['all', 'All leads'], ['new', 'New'], ['contacted', 'Contacted'], ['converted', 'Converted'], ['lost', 'Lost']];
const STATUSES = ['new', 'contacted', 'converted', 'lost'];
const SOURCE_LABELS = { comment: 'Comment', message: 'Message', review: 'Review' };

const statusOf = (l) => l.status;
const searchFields = (l) => [l.contact_name, l.content_summary, l.source_type];

export default function LeadsPage() {
  const [showToast, toastNode] = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [tab, setTab] = useState('all');
  const [q, setQ] = useState('');
  const { sort, onSort, apply } = useSort('timestamp', 'desc');

  const load = useCallback(() =>
    api.getLeads()
      .then(r => { setItems(r.items || []); setLastUpdated(new Date()); })
      .catch(() => showToast('Could not load leads', 'error'))
      .finally(() => setLoading(false)), [showToast]);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (id, status) => {
    try {
      await api.updateLead(id, status);
      setItems(prev => prev.map(x => x.lead_id === id ? { ...x, status } : x));
      showToast('Lead updated');
    } catch (e) {
      showToast(e.message || 'Could not update the lead', 'error');
    }
  };

  const { counts, filtered } = useListFilter(items, { statusOf, searchFields, tab, q });
  const sorted = apply(filtered, (l, col) => l[col] || '');
  const { pageRows, pagerProps } = usePager(sorted, [tab, q, sort.col, sort.dir]);

  return (
    <>
      <PageHeader
        title="Leads"
        subtitle="People who asked about care in a comment, message or review."
        meta={lastUpdated ? `Updated ${lastUpdated.toLocaleTimeString()}` : null}
        actions={<button type="button" className="ops-btn" onClick={load} title="Refresh"><Icon name="refresh" /> Refresh</button>}
      />

      {!loading && items.length > 0 && (
        <div className="ops-kpis ops-kpis--4">
          <StatCard label="Total leads" value={counts.all} />
          <StatCard label="New" value={counts.new || 0} tone={counts.new > 0 ? 'amber' : undefined} />
          <StatCard label="Contacted" value={counts.contacted || 0} tone="blue" />
          <StatCard label="Converted" value={counts.converted || 0} tone="green" />
        </div>
      )}

      <Panel flush
        title={<Tabs tabs={TABS} active={tab} counts={counts} onChange={setTab} />}
        action={<div className="ops-search"><Icon name="search" /><input className="ops-input" value={q} onChange={e => setQ(e.target.value)} placeholder="Search leads" aria-label="Search leads" /></div>}
        foot={!loading && sorted.length > 0 ? <Pager {...pagerProps} label="leads" /> : null}>
        {loading ? <Skeleton rows={6} />
          : items.length === 0 ? <Empty title="No leads yet">Leads appear here when someone asks about care on the Facebook Page.</Empty>
            : sorted.length === 0 ? <Empty title="No matching leads">Nothing matches this search or filter.</Empty> : (
              <div className="ops-table-wrap">
                <table className="ops-table">
                  <thead>
                    <tr>
                      <SortTh col="timestamp" label="Date" sort={sort} onSort={onSort} />
                      <SortTh col="contact_name" label="Name" sort={sort} onSort={onSort} />
                      <th>Source</th>
                      <th>What they said</th>
                      <SortTh col="status" label="Status" sort={sort} onSort={onSort} />
                      <th>Update</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pageRows.map(l => (
                      <tr key={l.lead_id}>
                        <td className="mono muted">{toEasternDateShort(l.timestamp)}</td>
                        <td className="ops-cell-main">{l.contact_name || 'Unknown'}</td>
                        <td><StatusPill status={l.source_type} label={SOURCE_LABELS[l.source_type] || l.source_type} /></td>
                        <td className="muted" style={{ maxWidth: 420, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={l.content_summary}>{l.content_summary}</td>
                        <td><StatusPill status={l.status} /></td>
                        <td>
                          <select className="ops-select ops-select--sm" value={l.status} aria-label={`Status for ${l.contact_name || 'lead'}`}
                            onChange={e => updateStatus(l.lead_id, e.target.value)}>
                            {STATUSES.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
      </Panel>
      {toastNode}
    </>
  );
}
